import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { BookingTypesStore, VenuesStore } from '@fry/booking/lib';
import { RolesStore } from '@fry/lib/roles';
import { iCaseCompare } from '@fry/lib/utils';
import { PaymentMethodsStore } from '@fry/payments/lib';
import { CurrencyService } from '@fry/payments/lib/currency';

import {
  EditorBookingItemTreeStore
} from '../editor-booking-item-tree/editor-booking-item-tree.store';
import { ConnectionsStore } from "@fry/connections/lib/connections.store";
import {
  ConnectionType,
  connectionTypeNamesById,
  ExamTypeEnum,
  RisrAssessBlueprint,
  RisrAssessItemSet
} from "@fry/connections/lib/connection";


@Injectable()
export class EditorDataService {
  hourNames: { id: number, name: string }[];
  minuteNames: { id: number, name: string }[];
  tzNames: { id: string, name: string }[];

  bookingItemNames$: Observable<{ id: string, name: string }[]>;
  bookingTypeNames$: Observable<{ id: string, name: string }[]>;
  risrAssessConnections$: Observable<{ id: string, name: string }[]>;
  roleNames$: Observable<{ id: string, name: string }[]>;
  currencyNames$: Observable<{ id: string, name: string }[]>;
  paymentMethodNames$: Observable<{ id: string, name: string }[]>;
  venueNames$: Observable<{ id: string, name: string }[]>;
  eligibilityRulesNames$: Observable<{ id: string, name: string }[]>;
  workflowNames$: Observable<{ id: string, name: string }[]>;

  constructor(
    private itemTreeStore: EditorBookingItemTreeStore,
    private bookingTypes: BookingTypesStore,
    private connections: ConnectionsStore,
    private roles: RolesStore,
    private paymentMethods: PaymentMethodsStore,
    private venues: VenuesStore,
    private currencyService: CurrencyService
  ) {
    this.hourNames = [{ id: null, name: '' }].concat(
      Array(24)
      .fill(null)
      .map((_, index) => ({ id: index, name: index.toString().padStart(2, '0') }))
    );
    this.minuteNames = [{ id: null, name: '' }].concat(
      Array(60)
        .fill(null)
        .map((_, index) => ({ id: index, name: index.toString().padStart(2, '0') }))
    );
    this.tzNames = moment.tz
      .names()
      .map(zone => ({ id: zone, name: zone }));

    this.bookingItemNames$ = this.getBookingItemNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.bookingTypeNames$ = this.getBookingTypeNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.risrAssessConnections$ = this.getRisrAssessConnections$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.roleNames$ = this.getRoleNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.currencyNames$ = this.getCurrencyNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.paymentMethodNames$ = this.getPaymentMethodNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.venueNames$ = this.getVenueNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.eligibilityRulesNames$ = this.getEligibilityRulesNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.workflowNames$ = this.getWorkflowNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
  }

  private getBookingItemNames$(): Observable<{ id: string, name: string }[]> {
    return this.itemTreeStore.itemTree$.pipe(
      map(itemTree =>
        itemTree.itemList
          .map(item => ({ id: item.id, name: item.name }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getBookingTypeNames$(): Observable<{ id: string, name: string }[]> {
    return this.bookingTypes.allStub();
  }

  private getRisrAssessConnections$(): Observable<{ id: string, name: string }[]> {
    return this.connections.allByConnectionType(ConnectionType.RISR_ASSESS).pipe(
      map(connections =>
        connections
          .filter(connection => connection.connectivityStatus === "ok")
          .map(connection => ({
            id: connection.id,
            name: connectionTypeNamesById[connection.connectionType] + " - " + connection.host
          }))
      )
    );
  }

  public getRisrAssessBlueprints(connectionId: string, examType: ExamTypeEnum): Observable<{ id: string, name: string }[]> {
    return this.connections.getRisrAssessBlueprints(connectionId, examType).pipe(
      map(blueprints =>
        blueprints.map((blueprint: RisrAssessBlueprint) => ({
          id: blueprint.id,
          name: blueprint.name
        }))
      )
    );
  }

  public getRisrAssessItemSets(connectionId: string, blueprintId: string): Observable<{ id: string, name: string }[]> {
    return this.connections.getRisrAssessItemSets(connectionId, blueprintId).pipe(
      map(itemSets =>
        itemSets.map((itemSet: RisrAssessItemSet) => ({
          id: itemSet.id,
          name: itemSet.name
        }))
      )
    );
  }

  private getRoleNames$(): Observable<{ id: string, name: string }[]> {
    return this.roles.allWithSystem().pipe(
      map(roles =>
        roles
          .filter(role => role.doc.contentRestriction === 'restricted')
          .map(role => ({ id: role.doc._id, name: role.doc.title }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getCurrencyNames$(): Observable<{ id: string, name: string }[]> {
    return this.currencyService.allowedCurrencies$().pipe(
      map(currencies => currencies.map(c => { return {id: c.code, name: c.code}; })
                                  .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getPaymentMethodNames$(): Observable<{ id: string, name: string }[]> {
    return this.paymentMethods.allStub();
  }

  private getVenueNames$(): Observable<{ id: string, name: string }[]> {
    return this.venues.allStub();
  }

  private getEligibilityRulesNames$(): Observable<{ id: string, name: string }[]> {
    return this.itemTreeStore.bookingItem$.pipe(
      map(bookingItem =>
        bookingItem.doc.eligibilities
          .map(eligibility => ({ id: eligibility.id, name: eligibility.name }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getWorkflowNames$(): Observable<{ id: string, name: string }[]> {
    return this.itemTreeStore.bookingItem$.pipe(
      map(bookingItem =>
        bookingItem.doc.workflows
          .map(workflow => ({ id: workflow.id, name: workflow.name }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }
}
